import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import api from '../services/api';
import { useDateFormat } from '../hooks/useDateFormat';
import { getUserTimezone, getTimezoneDisplayName } from '../utils/dateUtils';
import { workingRuleTypeLabel } from '../utils/workingCalendarLabels';
import { summarizeWorkingRule } from '../utils/workingCalendarRuleSummary';
import { WORKING_RULE_TYPES } from './UserWorkingCalendarPanel';
import {
  type RuleRow,
  cloneRuleRow,
  WorkingCalendarYearPreview,
  WorkingCalendarRuleEditDialog,
  DualViewDialogTitle,
  SectionLabelWithHint,
  WORKING_CALENDAR_DUAL_VIEW_DIALOG_PAPER_PROPS,
  WORKING_CALENDAR_DUAL_VIEW_CONTENT_SX,
  WORKING_CALENDAR_DUAL_VIEW_GRID_SX,
  WORKING_CALENDAR_PREVIEW_PANEL_SX,
  WORKING_CALENDAR_RULES_SCROLL_SX
} from './workingCalendar/workingCalendarDualViewShared';

type CalRow = {
  id: string;
  name: string;
  archived: boolean;
  updatedAt: string;
};

const FIRST_DAY_OF_WEEK_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' }
];

type Props = {
  organizationId: string;
  canManage: boolean;
};

const WorkingCalendarsSettingsPanel: React.FC<Props> = ({ organizationId, canManage }) => {
  const { formatDateTime, preferences } = useDateFormat();
  const userTz = getUserTimezone(preferences);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [rows, setRows] = useState<CalRow[]>([]);
  const [defaultId, setDefaultId] = useState<string | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editArchived, setEditArchived] = useState(false);
  const [editFirstDayOfWeek, setEditFirstDayOfWeek] = useState(1);
  const [editRules, setEditRules] = useState<RuleRow[]>([]);
  const [previewMap, setPreviewMap] = useState<Record<string, { working: boolean; expectedHours: number }>>({});
  const [viewYear, setViewYear] = useState(() => new Date().getFullYear());

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');

  const [ruleModalIndex, setRuleModalIndex] = useState<number | null>(null);
  const [ruleModalSource, setRuleModalSource] = useState<RuleRow | null>(null);
  const ruleModalIndexRef = useRef<number | null>(null);
  ruleModalIndexRef.current = ruleModalIndex;

  const loadList = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    setError(null);
    try {
      const [listRes, defRes] = await Promise.all([
        api.listWorkingCalendars(organizationId, includeArchived),
        api.getWorkingCalendarDefault(organizationId)
      ]);
      const list = (listRes?.data ?? listRes) as CalRow[];
      setRows(Array.isArray(list) ? list : []);
      const d = defRes?.data ?? defRes;
      setDefaultId((d?.defaultWorkingCalendarId as string) || null);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || 'Failed to load calendars');
    } finally {
      setLoading(false);
    }
  }, [organizationId, includeArchived]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const loadPreview = useCallback(async () => {
    if (!organizationId || !editingId) return;
    const from = `${viewYear}-01-01`;
    const to = `${viewYear}-12-31`;
    try {
      const res = await api.getWorkingCalendarPreview(organizationId, editingId, {
        from,
        to,
        timezone: userTz
      });
      const days = (res?.data?.days ?? res?.days) as Array<{
        date: string;
        working: boolean;
        expectedHours: number;
      }>;
      const m: Record<string, { working: boolean; expectedHours: number }> = {};
      (Array.isArray(days) ? days : []).forEach((d) => {
        m[d.date] = { working: d.working, expectedHours: d.expectedHours };
      });
      setPreviewMap(m);
    } catch {
      setPreviewMap({});
    }
  }, [organizationId, editingId, viewYear, userTz]);

  useEffect(() => {
    if (editorOpen && editingId) loadPreview();
  }, [editorOpen, editingId, loadPreview]);

  const closeRuleModal = () => {
    setRuleModalIndex(null);
    setRuleModalSource(null);
  };

  const openRuleModal = (index: number) => {
    const r = editRules[index];
    if (!r) return;
    setRuleModalIndex(index);
    setRuleModalSource(cloneRuleRow(r));
  };

  useEffect(() => {
    if (ruleModalIndex === null) {
      setRuleModalSource(null);
      return;
    }
    const r = editRules[ruleModalIndex];
    if (!r) {
      setRuleModalSource(null);
      return;
    }
    const next = cloneRuleRow(r);
    setRuleModalSource((prev) =>
      prev && JSON.stringify(prev) === JSON.stringify(next) ? prev : next
    );
  }, [editRules, ruleModalIndex]);

  const openEditor = async (id: string) => {
    setError(null);
    closeRuleModal();
    try {
      const res = await api.getWorkingCalendar(organizationId, id);
      const d = res?.data ?? res;
      const cal = d?.calendar;
      const rules = (d?.rules as Array<{ sortOrder: number; ruleType: string; payload: unknown }>) || [];
      setEditingId(id);
      setEditName(cal?.name || '');
      setEditArchived(!!cal?.archived);
      const fdow = cal?.firstDayOfWeek;
      setEditFirstDayOfWeek(typeof fdow === 'number' && fdow >= 0 && fdow <= 6 ? fdow : 1);
      setEditRules(
        rules.map((r) => ({
          sortOrder: r.sortOrder,
          ruleType: r.ruleType,
          payload: (r.payload as Record<string, unknown>) || {}
        }))
      );
      setEditorOpen(true);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || 'Failed to open calendar');
    }
  };

  const saveMeta = async () => {
    if (!editingId || !canManage) return;
    try {
      await api.patchWorkingCalendar(organizationId, editingId, {
        name: editName.trim(),
        archived: editArchived,
        firstDayOfWeek: editFirstDayOfWeek
      });
      setSuccess('Calendar updated.');
      await loadList();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || 'Failed to save');
    }
  };

  const saveRules = async () => {
    if (!editingId || !canManage) return;
    try {
      const sorted = editRules.map((r, i) => ({
        sortOrder: i,
        ruleType: r.ruleType,
        payload: r.payload
      }));
      await api.putWorkingCalendarRules(organizationId, editingId, sorted);
      setSuccess('Rules saved.');
      await loadPreview();
      await loadList();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || 'Failed to save rules');
    }
  };

  const removeRow = async (id: string) => {
    if (!canManage) return;
    if (!window.confirm('Delete this calendar? Only allowed when unused.')) return;
    try {
      await api.deleteWorkingCalendar(organizationId, id);
      setSuccess('Deleted.');
      await loadList();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || 'Cannot delete');
    }
  };

  const setDefault = async (id: string | null) => {
    if (!canManage) return;
    try {
      await api.patchWorkingCalendarDefault(organizationId, id);
      setDefaultId(id);
      setSuccess(id ? 'Default calendar updated.' : 'Default cleared.');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || 'Failed to set default');
    }
  };

  const createCalendar = async () => {
    if (!createName.trim()) return;
    try {
      const res = await api.createWorkingCalendar(organizationId, { name: createName.trim() });
      const d = res?.data ?? res;
      const id = d?.calendar?.id as string;
      setCreateOpen(false);
      setCreateName('');
      if (id) await openEditor(id);
      await loadList();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || 'Failed to create');
    }
  };

  const addRule = (ruleType: string) => {
    let payload: Record<string, unknown> = { kind: ruleType };
    if (ruleType === WORKING_RULE_TYPES.WEEKDAY_HOURS) {
      payload = { kind: 'WEEKDAY_HOURS', weekdays: [1, 2, 3, 4, 5], hours: 8 };
    } else if (ruleType === WORKING_RULE_TYPES.ANNUAL_OFF) {
      payload = {
        kind: 'ANNUAL_OFF',
        startMonth: 12,
        startDay: 25,
        endMonth: 12,
        endDay: 25
      };
    } else if (ruleType === WORKING_RULE_TYPES.DATE_OFF) {
      payload = { kind: 'DATE_OFF', date: `${viewYear}-01-01` };
    } else if (ruleType === WORKING_RULE_TYPES.DATE_HOURS) {
      payload = { kind: 'DATE_HOURS', date: `${viewYear}-01-01`, hours: 4 };
    }
    setEditRules((prev) => {
      const idx = prev.length;
      const newRow: RuleRow = { sortOrder: idx, ruleType, payload };
      setRuleModalIndex(idx);
      setRuleModalSource(cloneRuleRow(newRow));
      return [...prev, newRow];
    });
  };

  const commitRuleFromModal = (row: RuleRow) => {
    if (ruleModalIndex === null) return;
    setEditRules((prev) =>
      prev.map((r, i) =>
        i === ruleModalIndex ? { ...r, ruleType: row.ruleType, payload: row.payload } : r
      )
    );
  };

  const moveRule = (index: number, dir: -1 | 1) => {
    setEditRules((prev) => {
      const j = index + dir;
      if (j < 0 || j >= prev.length) return prev;
      const cp = [...prev];
      [cp[index], cp[j]] = [cp[j]!, cp[index]!];
      return cp;
    });
    setRuleModalIndex((cur) => {
      if (cur === null) return null;
      if (cur === index) return index + dir;
      if (cur === index + dir) return index;
      return cur;
    });
  };

  const removeRule = (index: number) => {
    const curModal = ruleModalIndexRef.current;
    if (curModal === index) {
      setRuleModalIndex(null);
      setRuleModalSource(null);
    } else if (curModal !== null && curModal > index) {
      setRuleModalIndex(curModal - 1);
    }
    setEditRules((prev) => prev.filter((_, i) => i !== index));
  };

  if (loading && rows.length === 0) return <LinearProgress />;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {!canManage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Only organization owners and managers can edit working calendars.
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
        <FormControlLabel
          control={
            <Switch checked={includeArchived} onChange={(_, v) => setIncludeArchived(v)} disabled={!canManage} />
          }
          label="Show archived"
        />
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            New calendar
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Archived</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell>Default</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{r.name}</TableCell>
                <TableCell>{r.archived ? 'Yes' : 'No'}</TableCell>
                <TableCell>{formatDateTime(r.updatedAt)}</TableCell>
                <TableCell>
                  {defaultId === r.id ? (
                    <Typography variant="body2">Yes</Typography>
                  ) : (
                    canManage &&
                    !r.archived && (
                      <Button size="small" onClick={() => setDefault(r.id)}>
                        Set default
                      </Button>
                    )
                  )}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEditor(r.id)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {canManage && defaultId !== r.id && (
                    <Tooltip title="Delete if unused">
                      <IconButton size="small" onClick={() => removeRow(r.id)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {canManage && defaultId && (
        <Button sx={{ mt: 2 }} size="small" onClick={() => setDefault(null)}>
          Clear organization default
        </Button>
      )}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New working calendar</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={createCalendar}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editorOpen}
        onClose={() => {
          closeRuleModal();
          setEditorOpen(false);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={WORKING_CALENDAR_DUAL_VIEW_DIALOG_PAPER_PROPS}
      >
        <DualViewDialogTitle hint="Edit calendar name, archive flag, and rules. Save rules to update the year preview.">
          Edit working calendar
        </DualViewDialogTitle>
        <DialogContent dividers sx={WORKING_CALENDAR_DUAL_VIEW_CONTENT_SX}>
          <Grid container spacing={2} sx={WORKING_CALENDAR_DUAL_VIEW_GRID_SX}>
            <Grid item xs={12} md={5} sx={WORKING_CALENDAR_PREVIEW_PANEL_SX}>
              <WorkingCalendarYearPreview
                viewYear={viewYear}
                onViewYearChange={setViewYear}
                previewMap={previewMap}
                firstDayOfWeek={editFirstDayOfWeek}
                timezoneLabel={getTimezoneDisplayName(userTz)}
                previewHelpExtra="First day of week affects this grid only (not stored rules)."
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={7}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: { xs: 380, md: 580 },
                flex: { md: '1 1 0' },
                maxWidth: { md: '58.333%' }
              }}
            >
              <Box sx={{ flexShrink: 0 }}>
                <TextField
                  label="Name"
                  fullWidth
                  size="small"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  sx={{ mb: 1 }}
                  disabled={!canManage}
                />
                <FormControl fullWidth size="small" sx={{ mb: 1 }} disabled={!canManage}>
                  <InputLabel id="fdow-label">First day of week (preview)</InputLabel>
                  <Select
                    labelId="fdow-label"
                    label="First day of week (preview)"
                    value={editFirstDayOfWeek}
                    onChange={(e: SelectChangeEvent<number>) =>
                      setEditFirstDayOfWeek(Number(e.target.value))
                    }
                  >
                    {FIRST_DAY_OF_WEEK_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editArchived}
                      onChange={(_, v) => setEditArchived(v)}
                      disabled={!canManage}
                    />
                  }
                  label="Archived"
                />
                {canManage && (
                  <Button size="small" onClick={saveMeta} sx={{ display: 'block', mb: 1, mt: 0.5 }}>
                    Save name / archive / week start
                  </Button>
                )}
              </Box>
              <Box sx={WORKING_CALENDAR_RULES_SCROLL_SX}>
                <SectionLabelWithHint
                  label="Rules (later wins)"
                  hint="Rules run top to bottom. Later numbers override earlier ones on the same day."
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                  <Button size="small" variant="outlined" onClick={() => addRule(WORKING_RULE_TYPES.WEEKDAY_HOURS)}>
                    + {workingRuleTypeLabel(WORKING_RULE_TYPES.WEEKDAY_HOURS)}
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => addRule(WORKING_RULE_TYPES.ANNUAL_OFF)}>
                    + {workingRuleTypeLabel(WORKING_RULE_TYPES.ANNUAL_OFF)}
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => addRule(WORKING_RULE_TYPES.DATE_OFF)}>
                    + {workingRuleTypeLabel(WORKING_RULE_TYPES.DATE_OFF)}
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => addRule(WORKING_RULE_TYPES.DATE_HOURS)}>
                    + {workingRuleTypeLabel(WORKING_RULE_TYPES.DATE_HOURS)}
                  </Button>
                </Box>
                {editRules.map((r, i) => (
                  <Paper
                    key={i}
                    variant="outlined"
                    sx={{
                      py: 0.75,
                      px: 1,
                      mb: 0.75,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                      <Box component="span" fontWeight={600}>
                        {i + 1}.
                      </Box>{' '}
                      {workingRuleTypeLabel(r.ruleType)}: {summarizeWorkingRule(r)}
                    </Typography>
                    {canManage && (
                      <Box sx={{ display: 'flex', flexShrink: 0, alignItems: 'center' }}>
                        <Tooltip title="Move up">
                          <span>
                            <IconButton size="small" onClick={() => moveRule(i, -1)} disabled={i === 0}>
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Move down">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => moveRule(i, 1)}
                              disabled={i === editRules.length - 1}
                            >
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openRuleModal(i)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton size="small" onClick={() => removeRule(i)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Paper>
                ))}
                {canManage && (
                  <Button variant="contained" onClick={saveRules} sx={{ mt: 1 }}>
                    Save rules
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0 }}>
          <Button
            onClick={() => {
              closeRuleModal();
              setEditorOpen(false);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <WorkingCalendarRuleEditDialog
        open={ruleModalIndex !== null && ruleModalSource !== null}
        source={ruleModalSource}
        canManage={canManage}
        onClose={closeRuleModal}
        onSave={commitRuleFromModal}
      />
    </Box>
  );
};

export default WorkingCalendarsSettingsPanel;
